import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AppContext } from "../AppContextProvider";
import { useWalletProvider } from "../walletprovider/useWalletProvider";
import ProfileJsonStorage from "../utils/storage/profileJsonStorage";
import { useAvalancheProvider } from "../avalanche/AvalancheProvider";
import Portal, { BackupMethods } from "@portal-hq/web";
import { hasSavedUserShares, loadUserShares } from "../utils/dwclient/localstorage/userShareStorage";
import { hasSavedClientId, loadClientId } from "../utils/dwclient/localstorage/clientIdStorage";
import { hasSavedClientSessionToken, loadClientSessionToken, } from "../utils/dwclient/localstorage/clientSessionTokenStorage";
import { createEmptyLogsAndVars } from "../utils/diagnostics/clientErrors";
import { deleteJwt, hasSavedJwt, storeJwt } from "../utils/dwclient/localstorage/jwtStorage";
import { createAuthTokenProcess, recoverMpcWalletProcess, } from "../processes/onboarding/onboarding";
import { createMpcWalletProcess } from "../processes/onboarding/createMpcWallet";
import { hasSavedSymmetricKey } from "../utils/dataEncryption";
import { waitForConditionAsync } from "../utils/zip/waitforcondition";
import { isCurrentBackendAuthCompletion } from "../walletprovider/walletWhitelist";
import { requestAuthTokenRefresh, validateSavedAuthToken } from "../utils/dwclient/refreshToken";
import { loadRefreshToken, storeRefreshToken } from "../utils/dwclient/localstorage/jwtStorage";
import { clearDesktopAuthWallet, isDesktopRefreshBindingCurrent, loadDesktopAuthWallet, storeDesktopAuthWallet, } from "../walletprovider/desktopAuthBinding";
const portalCompanyBackend = process.env.PORTAL_COMPANY_BACKEND ?? "https://q1.interb2b.com:8749";
export const DwMpcWalletBackendProviderContext = createContext({
    mpcWalletClientId: undefined,
    mpcWalletClientSessionToken: undefined,
    portalObjCreated: false,
    walletExists: false,
    ethAddress: "",
    solAddress: "",
    storageProps: {
        portalClientId: undefined,
        clientSessionToken: undefined,
        ed25519UserShare: undefined,
        loaded: false,
    },
    whenOnboardingFinished: (_callback) => { },
    /*refreshMpcWalletCST: () => {},
    sign: (msg: string) => Promise.resolve(undefined),
    encodeToHex: (msg: string) => "default",
    backupMpcWallet: (backupPassword: string) => {},
    recoverMpcWallet: (backupPassword: string) => {},
    deleteMpcWallet: () => {},
    sendTransaction: (to: string, amount: string) => Promise.resolve(),*/
});
export const DwMpcWalletBackendProvider = ({ children, onClientIdCreated, onClientIdCreateError, onClientIdSaved, onClientIdSaveError, onCSTRefreshed, onCSTRefreshError, onAcquirePortalAddressesError, }) => {
    const [mpcWalletClientId, setMpcWalletClientId] = useState(undefined);
    const [mpcWalletClientSessionToken, setMpcWalletClientSessionToken] = useState(undefined);
    const [portal, setPortal] = useState(undefined);
    const [portalObjCreated, setPortalObjCreated] = useState(false);
    const [walletExists, setWalletExists] = useState(undefined);
    const [ethAddress, setEthAddress] = useState(undefined);
    const [solAddress, setSolAddress] = useState(undefined);
    const [multiUserShares, setMultiUserShares] = useState(undefined);
    const [silentAuthRetryAttempt, setSilentAuthRetryAttempt] = useState(0);
    const { selectedWallet } = useWalletProvider();
    const walletProvider = useWalletProvider();
    const liveWalletSessionRef = useRef({
        account: walletProvider.selectedAccount?.toLowerCase() ?? null,
        version: walletProvider.walletSessionVersion,
    });
    liveWalletSessionRef.current = {
        account: walletProvider.selectedAccount?.toLowerCase() ?? null,
        version: walletProvider.walletSessionVersion,
    };
    const avalancheProvider = useAvalancheProvider();
    const profileJsonStorage = ProfileJsonStorage.create(avalancheProvider, walletProvider);
    const context = useContext(AppContext);
    useEffect(() => {
        const authenticatedAccount = walletProvider.selectedAccount;
        const authenticatedSessionVersion = walletProvider.walletSessionVersion;
        let cancelled = false;
        let retryTimer;
        const isCurrentAuthSession = () => !cancelled &&
            !!authenticatedAccount &&
            isCurrentBackendAuthCompletion(authenticatedAccount, authenticatedSessionVersion, liveWalletSessionRef.current.account, liveWalletSessionRef.current.version);
        async function hydrateSavedMpcWalletState() {
            if (!selectedWallet?.provider || !hasSavedSymmetricKey())
                return false;
            try {
                const savedMpcWalletData = await loadMpcWalletData(selectedWallet.provider);
                if (!savedMpcWalletData.clientId
                    || !savedMpcWalletData.clientSessionToken
                    || !savedMpcWalletData.userSharesString) {
                    return false;
                }
                const parsedUserShares = JSON.parse(savedMpcWalletData.userSharesString);
                if (!parsedUserShares?.ED25519?.share
                    || typeof parsedUserShares.ED25519.share !== "string") {
                    return false;
                }
                if (!isCurrentAuthSession())
                    return false;
                setMpcWalletClientId(savedMpcWalletData.clientId);
                setMpcWalletClientSessionToken(savedMpcWalletData.clientSessionToken);
                setMultiUserShares(parsedUserShares);
                return true;
            }
            catch {
                return false;
            }
        }
        async function jwtOnboarding() {
            //console.log(walletProvider.selectedAccount);
            //console.log("DwMpcWalletBackendProvider selectedWallet", selectedWallet);
            if (authenticatedAccount && selectedWallet !== null) {
                // A refresh token was issued by the signed onboarding flow and is
                // persisted specifically so an ordinary page reload can restore the
                // backend session without another wallet signature.
                const savedAccountMapRaw = localStorage.getItem("selectedAccountByWalletRdns");
                let savedAccount;
                try {
                    const savedAccountMap = savedAccountMapRaw ? JSON.parse(savedAccountMapRaw) : {};
                    savedAccount = savedAccountMap[selectedWallet.info.rdns];
                }
                catch {
                    savedAccount = undefined;
                }
                const exactSavedWallet = savedAccount?.toLowerCase() === authenticatedAccount.toLowerCase()
                    && loadDesktopAuthWallet() === authenticatedAccount.toLowerCase();
                const savedRefreshToken = loadRefreshToken();
                const savedAuthToken = localStorage.getItem("jwt");
                const refreshResult = exactSavedWallet && savedRefreshToken
                    ? await requestAuthTokenRefresh()
                    : { status: "no_token" };
                if (!isCurrentAuthSession())
                    return;
                if (refreshResult.status !== "no_token"
                    && !isDesktopRefreshBindingCurrent(authenticatedAccount, refreshResult.sourceRefreshToken)) {
                    return;
                }
                if (refreshResult.status === "success") {
                    await storeJwt(refreshResult.authToken);
                    if (!isCurrentAuthSession())
                        return;
                    if (refreshResult.refreshToken)
                        storeRefreshToken(refreshResult.refreshToken);
                    storeDesktopAuthWallet(authenticatedAccount);
                    const mpcStateRestored = await hydrateSavedMpcWalletState();
                    if (isCurrentAuthSession() && mpcStateRestored) {
                        walletProvider.setBackendAuthStatus("authorized", authenticatedAccount);
                    }
                    else if (isCurrentAuthSession()) {
                        walletProvider.setBackendAuthStatus("idle", authenticatedAccount);
                    }
                    return;
                }
                if (refreshResult.status === "no_token"
                    && exactSavedWallet
                    && savedAuthToken
                    && !walletProvider.allowInteractiveAuth) {
                    const validationResult = await validateSavedAuthToken(savedAuthToken);
                    if (!isCurrentAuthSession() || localStorage.getItem("jwt") !== savedAuthToken)
                        return;
                    if (validationResult.status === "success") {
                        const mpcStateRestored = await hydrateSavedMpcWalletState();
                        if (!isCurrentAuthSession() || localStorage.getItem("jwt") !== savedAuthToken)
                            return;
                        walletProvider.setBackendAuthStatus(mpcStateRestored ? "authorized" : "idle", authenticatedAccount);
                        return;
                    }
                    if (!walletProvider.allowInteractiveAuth) {
                        if (validationResult.status === "rejected") {
                            await deleteJwt();
                            clearDesktopAuthWallet();
                            walletProvider.setBackendAuthStatus("idle", authenticatedAccount);
                        }
                        else {
                            walletProvider.setBackendAuthStatus("checking", authenticatedAccount);
                            const retryDelay = Math.min(15000, 1500 * (2 ** Math.min(silentAuthRetryAttempt, 3)));
                            retryTimer = window.setTimeout(() => {
                                if (isCurrentAuthSession()) {
                                    setSilentAuthRetryAttempt((attempt) => attempt + 1);
                                }
                            }, retryDelay);
                        }
                        return;
                    }
                }
                if (!walletProvider.allowInteractiveAuth) {
                    if (refreshResult.status === "rejected") {
                        await deleteJwt();
                        clearDesktopAuthWallet();
                        walletProvider.setBackendAuthStatus("idle", authenticatedAccount);
                    }
                    else if (refreshResult.status === "no_token") {
                        walletProvider.setBackendAuthStatus("idle", authenticatedAccount);
                    }
                    else {
                        // A network timeout, 5xx response, or temporarily unreachable auth
                        // service is not proof that the wallet session was revoked. Keep
                        // protected routes in their restore state and retry the exact same
                        // wallet/token binding instead of navigating to /login.
                        walletProvider.setBackendAuthStatus("checking", authenticatedAccount);
                        const retryDelay = Math.min(15000, 1500 * (2 ** Math.min(silentAuthRetryAttempt, 3)));
                        retryTimer = window.setTimeout(() => {
                            if (isCurrentAuthSession()) {
                                setSilentAuthRetryAttempt((attempt) => attempt + 1);
                            }
                        }, retryDelay);
                    }
                    return;
                }
                const logsAndVars = createEmptyLogsAndVars("DwMpcWalletBackendProvider:jwtOnboarding");
                let mpcWalletData;
                if (selectedWallet?.provider) {
                    mpcWalletData = await loadMpcWalletData(selectedWallet?.provider);
                }
                else {
                    mpcWalletData = createEmptyMpcWalletData();
                }
                if (mpcWalletData.clientId === null || mpcWalletData.userSharesString === null) {
                    mpcWalletData = await recoverMpcWalletProcess(authenticatedAccount, mpcWalletData, selectedWallet);
                    if (mpcWalletData.clientId === null) {
                        mpcWalletData = await createMpcWalletProcess(authenticatedAccount, selectedWallet);
                    }
                }
                mpcWalletData = await createAuthTokenProcess(selectedWallet?.provider, authenticatedAccount, mpcWalletData);
                if (mpcWalletData.authToken) {
                    // This token was issued by the signed onboarding/auth endpoints.
                    // Only now can the route guard authorize this live wallet.
                    if (isCurrentAuthSession()) {
                        await storeJwt(mpcWalletData.authToken);
                        if (!isCurrentAuthSession())
                            return;
                        storeDesktopAuthWallet(authenticatedAccount);
                        walletProvider.setBackendAuthStatus("authorized", authenticatedAccount);
                    }
                }
                else if (isCurrentAuthSession()) {
                    // Missing local/default auth output is not server-authoritative
                    // evidence that this wallet is forbidden.
                    walletProvider.setBackendAuthStatus("checking", authenticatedAccount);
                }
                if (isCurrentAuthSession() && mpcWalletData.userSharesString !== null) {
                    setMultiUserShares(JSON.parse(mpcWalletData.userSharesString));
                }
            }
        }
        // Contract whitelist authorization is a separate prerequisite. Do not
        // start a backend signing/auth flow for a wallet that is denied or whose
        // whitelist RPC is unavailable; this also avoids gratuitous wallet
        // prompts while the on-chain proof is being established.
        if (walletProvider.selectedAccount
            && walletProvider.selectedAccount !== null
            && walletProvider.whitelistStatus === "authorized") {
            /*console.log("DwMpcWalletBackendProvider:isWalletChanged", walletProvider.isWalletChanged());
            if (walletProvider.isWalletChanged()) {
              sessionStorage.clear();
              deleteMpcData();
            }*/
            jwtOnboarding().catch((error) => {
                const status = error?.response?.status;
                if (authenticatedAccount && isCurrentAuthSession()) {
                    const authoritativeDenial = status === 401 || status === 403;
                    if (authoritativeDenial) {
                        walletProvider.setBackendAuthStatus("denied", authenticatedAccount, true);
                    }
                    else {
                        walletProvider.setBackendAuthStatus("checking", authenticatedAccount);
                        const retryDelay = Math.min(15000, 1500 * (2 ** Math.min(silentAuthRetryAttempt, 3)));
                        retryTimer = window.setTimeout(() => {
                            if (isCurrentAuthSession()) {
                                setSilentAuthRetryAttempt((attempt) => attempt + 1);
                            }
                        }, retryDelay);
                    }
                }
            });
        }
        return () => {
            cancelled = true;
            if (retryTimer !== undefined)
                window.clearTimeout(retryTimer);
        };
    }, [
        walletProvider.selectedAccount,
        walletProvider.walletSessionVersion,
        walletProvider.isConnected(),
        walletProvider.whitelistStatus,
        context.getState()?.isConnected,
        silentAuthRetryAttempt,
    ]);
    async function loadMpcWalletData(provider) {
        return {
            clientId: await loadClientId(provider),
            clientSessionToken: await loadClientSessionToken(provider),
            userSharesString: await loadUserShares(provider),
            authToken: null,
            ethWalletAddress: null,
            solanaWalletAddress: null,
        };
    }
    function createEmptyMpcWalletData() {
        return {
            clientId: null,
            clientSessionToken: null,
            userSharesString: null,
            authToken: null,
            ethWalletAddress: null,
            solanaWalletAddress: null,
        };
    }
    async function whenOnboardingFinished(_callback) {
        (async () => {
            await waitForConditionAsync(() => hasSavedSymmetricKey() &&
                hasSavedClientId() &&
                hasSavedClientSessionToken() &&
                hasSavedUserShares() &&
                hasSavedJwt());
            if (_callback !== undefined) {
                _callback();
            }
        })();
    }
    async function createMpcWalletClientId() {
        //console.log("selectedAccount", walletProvider.selectedAccount);
        if (walletProvider.selectedAccount !== null) {
            try {
                // console.log("invoking mpc wallet backend");
                const result = await axios.post(portalCompanyBackend + "/create");
                onClientIdCreated?.();
                return result.data.id;
            }
            catch (error) {
                // console.log(error);
                onClientIdCreateError?.();
                //showError("Portal client ID creation failed.");
                // TODO error callback
            }
        }
        else {
            // TODO és mi van, ha igen?
        }
        return undefined;
    }
    async function savePortalMpcWalletClientId(receivedPortalClientId) {
        if (walletProvider.selectedAccount !== null) {
            try {
                const profileDataUpdate = { nillionMpcWalletData: receivedPortalClientId };
                await profileJsonStorage.updateDataForAddress(walletProvider.selectedAccount, profileDataUpdate);
                onClientIdSaved?.();
            }
            catch (e) {
                onClientIdSaveError?.();
                throw new Error("MPC Wallet client id save failed");
            }
        }
    }
    async function refreshMpcWalletCST() {
        if (mpcWalletClientId === undefined) {
            return;
        }
        try {
            const refreshResultObj = await axios.post(portalCompanyBackend + "/refresh?clientId=" + mpcWalletClientId);
            setMpcWalletClientSessionToken(refreshResultObj.data.clientSessionToken);
            const portal = new Portal({
                apiKey: refreshResultObj.data.clientSessionToken,
                autoApprove: true,
                rpcConfig: {
                    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp": "https://api.mainnet-beta.solana.com",
                    "43113": "https://api.avax-test.network/ext/bc/C/rpc",
                    "eip155:43113": "https://api.avax-test.network/ext/bc/C/rpc",
                },
            });
            setPortal(portal);
            setPortalObjCreated(true);
            portal.onInitializationError((reason) => {
                //  console.log("portal", "onInitializationError:" + reason);
            });
            portal.onReady(async () => {
                //  console.log("portal", "onReady");
                const localWalletExists = await portal.doesWalletExist();
                // console.log("portal walletExists", localWalletExists);
                setWalletExists(localWalletExists);
                if (localWalletExists) {
                    await acquirePortalAddresses(portal);
                }
            });
            //console.log(portal);
            onCSTRefreshed?.();
            acquirePortalAddresses(portal);
            //showNotification("MPC wallet Client session token refreshed successfully.");
        }
        catch (error) {
            console.error("refresh error", error);
            onCSTRefreshError?.();
            //showError("Client session token refresh failed.");
        }
        //setPortal(portal);
    }
    async function acquirePortalAddresses(portalParam) {
        if (portalParam === undefined) {
            portalParam = portal;
        }
        if (portalParam !== undefined) {
            const localEthAddress = await portalParam.getEip155Address();
            const localSolAddress = await portalParam.getSolanaAddress();
            setEthAddress(localEthAddress);
            setSolAddress(localSolAddress);
            // console.log("portal ethAddress", localEthAddress);
            //  console.log("portal solAddress", localSolAddress);
        }
        else {
            // console.log("portal", "undefined");
            onAcquirePortalAddressesError?.();
            //showError("Please connect to Portal");
        }
    }
    async function backupMpcWallet(backupPassword) {
        try {
            //console.log("backup started");
            //console.log(portal);
            const cipherTextObj = await portal?.backupWallet(BackupMethods.password, (status) => {
                //  console.log(status.status);
                // console.log(status.done);
            }, {
                passwordStorage: {
                    password: backupPassword,
                },
            });
            // console.log("mpc wallet ciphertext", cipherTextObj?.cipherText);
            if (cipherTextObj !== undefined) {
                try {
                    await axios.post(portalCompanyBackend + "/mpcwalletbackup/user/backup/" + mpcWalletClientId, {
                        backupMethod: "PASSWORD",
                        cipherText: cipherTextObj.cipherText,
                    });
                    await portal?.storedClientBackupShare(true, BackupMethods.password);
                    //setBackupPassword("");
                    //showNotification("MPC wallet backup created successfully.");
                }
                catch (error) {
                    await portal?.storedClientBackupShare(false, BackupMethods.password);
                }
            }
        }
        catch (error) {
            console.error(error);
            //showError("MPC Wallet backup failed.");
        }
    }
    async function recoverMpcWallet(backupPassword) {
        try {
            // Get the encrypted user backup share from your API.
            const response = await axios.get(portalCompanyBackend + "/mpcwalletbackup/user/fetch/" + mpcWalletClientId + "?backupMethod=PASSWORD");
            const cipherText = response.data;
            // console.log(cipherText);
            // Recover replaces your current signing shares (if there are any) with new ones.
            await portal?.recoverWallet(cipherText, BackupMethods.password, {
                passwordStorage: { password: backupPassword },
            });
            //setBackupPassword("");
            //showNotification("MPC wallet recovered successfully.");
        }
        catch (error) {
            //  console.log(error);
            //showError("MPC Wallet recovery failed.");
        }
    }
    async function deleteMpcWallet() {
        try {
            portal?.clearLocalWallet();
            //showNotification("MPC wallet local user share deleted successfully.");
        }
        catch (error) {
            console.log(error);
            //showError("MPC Wallet user share removal failed.");
        }
    }
    async function getortalBalance(portal) {
        if (portal !== undefined) {
            const erc20Balances = await portal.getBalances("eip155:0xA869");
            // console.log("portal Balance", erc20Balances);
        }
        else {
            //  console.log("portal", "undefined");
        }
    }
    async function sign(msg) {
        if (portal) {
            const hexTextToSign = encodeToHex(msg);
            try {
                const transactionHash = await portal.request({
                    chainId: "eip155:43113",
                    method: "eth_sign",
                    params: [ethAddress, hexTextToSign],
                });
                //setSignTransactionHash(transactionHash);
                return transactionHash;
            }
            catch (error) {
                console.error(error);
                //showError("Sign transaction failed.");
                return undefined;
            }
        }
        return undefined;
    }
    function encodeToHex(text) {
        let hexString = "";
        for (let i = 0; i < text.length; i++) {
            hexString += text.charCodeAt(i).toString(16).padStart(2, "0");
        }
        return "0x" + hexString;
    }
    /*const sendTransaction = async (to: string, amount: string) => {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      try {
        await provider.send("wallet_switchEthereumChain", [{ chainId: "0xA869" }]); // 43113 in hex
      } catch (error: any) {
        if (error.code === 4902) {
          await provider.send("wallet_addEthereumChain", [
            {
              chainId: "0xA869",
              chainName: "Avalanche Fuji (C-Chain)",
              rpcUrls: ["https://api.avax-test.network/ext/C/rpc"],
              nativeCurrency: {
                name: "AVAX",
                symbol: "AVAX",
                decimals: 18,
              },
              blockExplorerUrls: ["https://testnet.snowtrace.io"],
            },
          ]);
        } else {
          console.error(error);
        }
      }
  
      const tx = {
        to: to,
        value: utils.parseEther(amount), // Convert amount to wei
        gasLimit: utils.hexlify(21000), // Gas limit for a standard transaction
        gasPrice: await provider.getGasPrice(), // Current gas price
      };
    };*/
    const contextValue = {
        //clientId,
        mpcWalletClientId,
        mpcWalletClientSessionToken,
        portalObjCreated,
        walletExists: walletExists ?? false,
        ethAddress: ethAddress ?? "",
        solAddress: solAddress ?? "",
        storageProps: {
            portalClientId: mpcWalletClientId, //mpcWalletClientId,
            clientSessionToken: mpcWalletClientSessionToken, //mpcWalletClientSessionToken,
            ed25519UserShare: multiUserShares?.ED25519?.share, //ed25519UserShare ?? "",
            loaded: multiUserShares !== undefined && hasSavedSymmetricKey(),
        },
        whenOnboardingFinished,
        /*refreshMpcWalletCST,
        sign,
        encodeToHex,
        backupMpcWallet,
        recoverMpcWallet,
        deleteMpcWallet,
        sendTransaction,*/
    };
    return (_jsx(DwMpcWalletBackendProviderContext.Provider, { value: contextValue, children: children }));
};
